import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/apiClient';
import { Card } from '@/components/ui/Card';

/**
 * Story 67.2 — Live session monitoring page.
 *
 * Connects to the SSE endpoint `/sse/voice/sessions/{id}/stream` and
 * displays utterances in real time as they arrive. Also shows the
 * session metadata and final outcome when the session ends.
 *
 * Route: /voice/sessions/:id
 */

interface SessionSummary {
  id: string;
  scriptId: string;
  direction: string;
  transport: string;
  fromNumber: string | null;
  toNumber: string | null;
  startedAt: string;
  endedAt: string | null;
  outcome: string | null;
}

interface Utterance {
  id: string;
  sessionId: string;
  speaker: 'AGENT' | 'PROSPECT';
  text: string;
  sentiment: number | null;
  startedAt: string;
  endedAt: string | null;
}

const SENTIMENT_LABEL = (s: number | null): string => {
  if (s === null) return '';
  if (s >= 0.3) return '😊';
  if (s <= -0.3) return '😟';
  return '😐';
};

const SPEAKER_CLASS: Record<string, string> = {
  AGENT: 'bg-primary/10 text-primary self-start',
  PROSPECT: 'bg-muted text-foreground self-end',
};

export function VoiceSessionMonitorPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const [session, setSession] = useState<SessionSummary | null>(null);
  const [utterances, setUtterances] = useState<Utterance[]>([]);
  const [live, setLive] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);
  const sseRef = useRef<EventSource | null>(null);

  // Load session metadata
  useEffect(() => {
    if (!id) return;
    apiClient.get<SessionSummary>(`/voice/sessions/${id}`).then((r) => {
      setSession(r.data);
      if (r.data.endedAt) setLive(false);
    });
    // Also load existing utterances (for post-call review)
    apiClient.get<Utterance[]>(`/voice/sessions/${id}/utterances`).then((r) => {
      setUtterances(r.data);
    });
  }, [id]);

  // Connect SSE for live streaming
  useEffect(() => {
    if (!id) return;
    if (!live) return;

    const token = (apiClient.defaults.headers.common['Authorization'] as string | undefined)?.replace('Bearer ', '');
    const url = `/api/sse/voice/sessions/${id}/stream${token ? `?token=${encodeURIComponent(token)}` : ''}`;
    const es = new EventSource(url);
    sseRef.current = es;

    es.addEventListener('utterance', (e) => {
      const utterance = JSON.parse((e as MessageEvent).data) as Utterance;
      setUtterances((prev) => {
        if (prev.some((u) => u.id === utterance.id)) return prev;
        return [...prev, utterance];
      });
    });

    es.addEventListener('session_end', () => {
      setLive(false);
      es.close();
      // Refresh session to get final outcome
      apiClient.get<SessionSummary>(`/voice/sessions/${id}`).then((r) => setSession(r.data));
    });

    es.onerror = () => {
      setLive(false);
      es.close();
    };

    return () => { es.close(); };
  }, [id, live]);

  // Auto-scroll to bottom on new utterances
  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [utterances.length]);

  if (!session) {
    return <div className="text-muted-foreground text-sm">{t('app.loading', 'Loading...')}</div>;
  }

  return (
    <div className="space-y-4 max-w-3xl mx-auto">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t('voice.sessions.title')}</h1>
        {live ? (
          <span className="flex items-center gap-1.5 text-sm text-emerald-600 font-medium">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
            </span>
            {t('voice.sessions.live')}
          </span>
        ) : (
          <span className="text-sm text-muted-foreground">{t('voice.sessions.ended')}</span>
        )}
      </div>

      <Card className="p-4 grid grid-cols-2 gap-3 text-sm">
        <div>
          <span className="font-medium">{t('voice.sessions.outcome')}:</span>{' '}
          {session.outcome ?? '—'}
        </div>
        <div>
          <span className="font-medium">{t('voice.routes.did')}:</span>{' '}
          <span className="font-mono">{session.toNumber ?? '—'}</span>
        </div>
        <div>
          <span className="font-medium">{t('nav.voiceScripts')}:</span>{' '}
          {session.scriptId}
        </div>
        <div>
          <span className="font-medium">{t('voice.sessions.title')} ID:</span>{' '}
          <span className="font-mono text-xs">{session.id}</span>
        </div>
      </Card>

      <Card className="p-4">
        <h2 className="font-medium mb-3">{t('voice.sessions.utterances')}</h2>
        {utterances.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('voice.sessions.empty')}</p>
        ) : (
          <div className="flex flex-col gap-2">
            {utterances.map((u) => (
              <div
                key={u.id}
                className={`rounded-lg px-3 py-2 max-w-[80%] text-sm ${SPEAKER_CLASS[u.speaker] ?? 'bg-muted'}`}
              >
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-medium text-xs">
                    {t(`voice.sessions.speaker.${u.speaker}`, u.speaker)}
                  </span>
                  {u.sentiment !== null && (
                    <span className="text-xs" title={String(u.sentiment)}>
                      {SENTIMENT_LABEL(u.sentiment)}
                    </span>
                  )}
                  <span className="text-xs text-muted-foreground ml-auto">
                    {new Date(u.startedAt).toLocaleTimeString()}
                  </span>
                </div>
                <p>{u.text}</p>
              </div>
            ))}
            <div ref={scrollRef} />
          </div>
        )}
      </Card>
    </div>
  );
}
